/**
 * Promotion Engine — H2HUMANS Cart Discount System
 *
 * The engine runs on every cart change and computes all applicable
 * discounts (both automatic and promo-code-based) at the line-item level.
 *
 * Two initial promotions:
 *   1. **FIRST20 (promo code):** 20% off any product (except bundle) on the
 *      first time it's purchased. On the frontend we apply it assuming
 *      first-time eligibility; backend validates against purchase history
 *      at checkout creation time.
 *   2. **Bundle auto 25%:** When a bundle item is in the cart OR the
 *      individual items make up a valid bundle, a 25% discount is applied
 *      to those items automatically (no code needed).
 */

import {
  BUNDLE,
  BUNDLE_PRODUCT_IDS,
  BUNDLE_REQUIREMENTS,
  type ProductId,
} from './products';
import type { CartItem } from '@/contexts/CartContext';
import { PROMOTIONS_CONFIG } from './promotions-config';
import { isProductId, priceSingleLine, type SingleLinePricing } from './product-catalog-data';

/* ─── Types ─── */

export type PromotionType =
  | 'first_purchase_individual'
  | 'first_purchase_bundle'
  | 'first_purchase_percentage'
  | 'quantity_tier';

export interface AppliedPromotion {
  type: PromotionType;
  /** Which items this promotion applies to (item ids, possibly the bundle id). */
  itemIds: string[];
  discountPercent: number;
  discountAmount: number;
  labelKey: string; // translation key prefix, e.g. 'promotions.bundle' or 'promotions.firstPurchase'
}

export interface CartTotals {
  /** Sum of item (price × quantity) with NO discounts applied. */
  subtotal: number;
  /** All currently active promotions on the cart. */
  promotions: AppliedPromotion[];
  /** Sum of all promotion discounts. */
  totalDiscount: number;
  /** Final total (subtotal − totalDiscount). */
  total: number;
  /** Per single-product line: which discount is active (higher one wins). */
  lines: Record<string, SingleLinePricing>;
}

/* ─── Helpers ─── */

/** Count how many complete bundle sets the individual items form. */
function getCompleteBundleCount(items: CartItem[]): number {
  const counts = BUNDLE_PRODUCT_IDS.map((id) => {
    const item = items.find((i) => i.id === id);
    const required = BUNDLE_REQUIREMENTS[id];
    return Math.floor((item?.quantity ?? 0) / required);
  });
  return Math.min(...counts);
}

/** Check if the cart has the explicit bundle product. */
function hasExplicitBundle(items: CartItem[]): boolean {
  return items.some((i) => i.id === BUNDLE.id);
}

/** Check if individual items satisfy all bundle requirements. */
function hasBundleEquivalent(items: CartItem[]): boolean {
  return BUNDLE_PRODUCT_IDS.every((id) => {
    const item = items.find((i) => i.id === id);
    const required = BUNDLE_REQUIREMENTS[id];
    return (item?.quantity ?? 0) >= required;
  });
}

/** Compute raw (undiscounted) subtotal. */
function rawSubtotal(items: CartItem[]): number {
  return items.reduce((sum, i) => sum + i.price * i.quantity, 0);
}

/* ─── Engine ─── */

export interface EngineInput {
  items: CartItem[];
  /** Resolved promo-code information from the backend (null if no code applied). */
  promoCode: {
    code: string;
    promotionType: PromotionType;
    discountPercent: number;
    stripePromotionCodeId: string;
    channel?: string | null;
  } | null;
}

/**
 * Run the promotion engine on the current cart.
 * Called every time items or promo code changes.
 */
export function evaluatePromotions(input: EngineInput): CartTotals {
  const { items, promoCode } = input;
  const promotions: AppliedPromotion[] = [];
  const subtotal = rawSubtotal(items);

  /* ── 1. Bundle auto-discount (disabled via config, kept for future use) ── */
  if (PROMOTIONS_CONFIG.bundle_percentage.enabled) {
    const explicitBundle = hasExplicitBundle(items);
    const bundleEquivalent = hasBundleEquivalent(items);

    if (explicitBundle || bundleEquivalent) {
      if (explicitBundle) {
        const bundleItem = items.find((i) => i.id === BUNDLE.id)!;
        const discountAmount = Math.round(bundleItem.price * bundleItem.quantity * 0.25 * 100) / 100;
        if (discountAmount > 0) {
          promotions.push({
            type: 'first_purchase_bundle',
            itemIds: [BUNDLE.id],
            discountPercent: 25,
            discountAmount,
            labelKey: 'promotions.bundle',
          });
        }
      } else if (bundleEquivalent) {
        const completeBundles = getCompleteBundleCount(items);
        let totalBundleDiscount = 0;
        const bundleItemIds: string[] = [];

        for (const pid of BUNDLE_PRODUCT_IDS) {
          const item = items.find((i) => i.id === pid);
          if (!item) continue;
          const required = BUNDLE_REQUIREMENTS[pid];
          const qty = completeBundles * required;
          if (qty <= 0) continue;
          const discount = Math.round(item.price * qty * 0.25 * 100) / 100;
          totalBundleDiscount += discount;
          bundleItemIds.push(pid);
        }

        if (totalBundleDiscount > 0) {
          promotions.push({
            type: 'first_purchase_bundle',
            itemIds: bundleItemIds,
            discountPercent: 25,
            discountAmount: totalBundleDiscount,
            labelKey: 'promotions.bundle',
          });
        }
      }
    }
  }

  /* ── 2. Quantity tier vs. promo code on single products ── */
  // Promo codes never apply to the already discounted Komplett-Set (bundle).
  // Quantity discount and code are not combinable: per line the higher wins.
  const lines: Record<string, SingleLinePricing> = {};
  const codeOnSingles =
    !!promoCode &&
    ((promoCode.promotionType === 'first_purchase_individual' && PROMOTIONS_CONFIG.first_purchase_individual.enabled) ||
      (promoCode.promotionType === 'first_purchase_percentage' && PROMOTIONS_CONFIG.first_purchase_percentage.enabled));
  const codePct = codeOnSingles ? promoCode!.discountPercent : 0;
  const tierIds: string[] = [];
  const codeIds: string[] = [];
  let tierDiscount = 0;
  let codeDiscount = 0;
  for (const item of items) {
    if (item.id === BUNDLE.id || !isProductId(item.id)) continue;
    const line = priceSingleLine(item.id, item.quantity, codePct);
    lines[item.id] = line;
    if (line.source === 'tier') { tierDiscount += line.discount; tierIds.push(item.id); }
    if (line.source === 'code') { codeDiscount += line.discount; codeIds.push(item.id); }
  }
  if (tierDiscount > 0) {
    promotions.push({ type: 'quantity_tier', itemIds: tierIds, discountPercent: 0, discountAmount: Math.round(tierDiscount * 100) / 100, labelKey: 'promotions.quantityTier' });
  }
  if (codeDiscount > 0 && promoCode) {
    promotions.push({ type: promoCode.promotionType, itemIds: codeIds, discountPercent: codePct, discountAmount: Math.round(codeDiscount * 100) / 100, labelKey: 'promotions.firstPurchase' });
  }

  const promoEligible = items.filter((i) => i.id !== BUNDLE.id);
  if (promoCode) {
    const { promotionType, discountPercent } = promoCode;

    // 20% off the bundle item only
    if (promotionType === 'first_purchase_bundle' && PROMOTIONS_CONFIG.first_purchase_bundle.enabled) {
      let promoDiscount = 0;
      const eligibleItemIds: string[] = [];

      for (const item of promoEligible) {
        if (item.id !== BUNDLE.id) continue;
        const discount = Math.round(item.price * item.quantity * (discountPercent / 100) * 100) / 100;
        promoDiscount += discount;
        eligibleItemIds.push(item.id);
      }

      if (promoDiscount > 0) {
        promotions.push({
          type: 'first_purchase_bundle',
          itemIds: eligibleItemIds,
          discountPercent,
          discountAmount: promoDiscount,
          labelKey: 'promotions.firstPurchaseBundle',
        });
      }
    }
  }

  /* ── Compute totals ── */
  const totalDiscount = promotions.reduce((sum, p) => sum + p.discountAmount, 0);
  const total = Math.max(0, subtotal - totalDiscount);

  return { subtotal, promotions, totalDiscount: Math.round(totalDiscount * 100) / 100, total: Math.round(total * 100) / 100, lines };
}